import { getPuckHistory } from './puckHistoryRegistry';

/**
 * Pile d'annulation unique de l'éditeur web, comme celle du classique : la
 * dernière modification s'annule d'abord, qu'elle vienne de la page ou du
 * thème du site.
 *
 * Le thème vit hors de Puck (projectConfig, valable pour tout le site) : son
 * historique ne peut pas être celui de Puck, qui ne connaît qu'une page. La
 * pile entrelace donc :
 * - `{ kind: 'puck', id }` : marqueur d'une entrée de l'historique Puck,
 *   rejouée par back/forward ;
 * - `{ kind: 'theme', before, after, location }` : le thème entier
 *   avant/après, et l'endroit du panneau où il a été réglé, où l'annulation
 *   ramène ;
 * - `{ kind: 'form', undo, redo }` : une valeur saisie dans la vue formulaire,
 *   écrite hors de Puck (il ne connaît que la page affichée) : chaque sens
 *   réécrit la valeur et ramène sur sa ligne du formulaire.
 *
 * Restaurer l'objet thème suffit : le lien vivant des blocs se calcule au
 * rendu (puck/themedBlocks), le thème n'écrit jamais dans les données Puck.
 */

// Même fenêtre que la capture du classique (undo/undoCapture.js) : un réglage
// continu (glisser une couleur) ne fait qu'une étape.
export const THEME_MERGE_MS = 150;

let undoStack = [];
let redoStack = [];
let puckInstance = null;
let seenPuckIds = new Set();
/** @type {{
 *   applyTheme: (theme: object, location: object|null) => void,
 *   showPage?: () => void,
 * } | null} */
let themeHost = null;
/** Où est le panneau Thème (WebThemePanel), null s'il est fermé. */
let themeLocation = null;
let state = { canUndo: false, canRedo: false };
const listeners = new Set();

function commit(nextUndo, nextRedo) {
  undoStack = nextUndo;
  redoStack = nextRedo;
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  if (canUndo === state.canUndo && canRedo === state.canRedo) return;
  state = { canUndo, canRedo };
  listeners.forEach((listener) => listener());
}

/**
 * Suit l'historique d'une instance Puck (PuckHistoryBridge). Chaque `record`
 * de Puck crée une entrée d'id neuf ; back/forward retombent sur des ids déjà
 * vus ; l'entrée initiale (index 0) n'est pas une modification.
 *
 * Une nouvelle instance (changement de page ou de section) repart d'un
 * historique vide : les marqueurs de l'ancienne ne désignent plus rien, les
 * modifications du thème restent. Le pont peut être remonté sur la même
 * instance (overrides recréés) : l'instance se reconnaît à son `back`.
 */
export function notePuckHistory(instance, id, index) {
  if (instance !== puckInstance) {
    puckInstance = instance;
    seenPuckIds = new Set();
    // Le thème et le formulaire s'écrivent hors de Puck : ils survivent à
    // l'instance.
    const outsidePuck = (entry) => entry.kind !== 'puck';
    commit(undoStack.filter(outsidePuck), redoStack.filter(outsidePuck));
  }
  if (!id || seenPuckIds.has(id)) return;
  seenPuckIds.add(id);
  if (index > 0) commit([...undoStack, { kind: 'puck', id }], []);
}

export function recordThemeChange(before, after, now = Date.now()) {
  if (before === after) return;
  const top = undoStack[undoStack.length - 1];
  if (top?.kind === 'theme' && now - top.at < THEME_MERGE_MS) {
    commit([...undoStack.slice(0, -1), { ...top, after, at: now }], []);
    return;
  }
  commit([...undoStack, {
    kind: 'theme', before, after, at: now, location: themeLocation,
  }], []);
}

export function recordFormChange({ undo: undoChange, redo: redoChange }) {
  commit([...undoStack, { kind: 'form', undo: undoChange, redo: redoChange }], []);
}

export function setThemeLocation(location) {
  themeLocation = location;
}

export function clearUndoTimeline() {
  puckInstance = null;
  seenPuckIds = new Set();
  commit([], []);
}

function apply(entry, direction) {
  if (entry.kind === 'form') {
    entry[direction]();
    return;
  }
  if (entry.kind === 'theme') {
    themeHost?.applyTheme(direction === 'undo' ? entry.before : entry.after, entry.location);
    return;
  }
  const history = getPuckHistory();
  if (direction === 'undo') history?.undo();
  else history?.redo();
  // Comme la navigation du classique : on montre la page où le changement a lieu.
  themeHost?.showPage?.();
}

function undoSettled() {
  const entry = undoStack[undoStack.length - 1];
  if (!entry) return;
  commit(undoStack.slice(0, -1), [...redoStack, entry]);
  apply(entry, 'undo');
}

function redoSettled() {
  const entry = redoStack[redoStack.length - 1];
  if (!entry) return;
  commit([...undoStack, entry], redoStack.slice(0, -1));
  apply(entry, 'redo');
}

function afterHistoryFlush(action) {
  const history = getPuckHistory();
  if (!history?.flush) return action();
  return history.flush().then(() => {
    if (getPuckHistory()?.undo === history.undo) action();
  });
}

export function undo() {
  return afterHistoryFlush(undoSettled);
}

export function redo() {
  return afterHistoryFlush(redoSettled);
}

/** L'éditeur qui porte le thème ; renvoie le désenregistrement. */
export function registerThemeHost(host) {
  themeHost = host;
  return () => {
    if (themeHost === host) themeHost = null;
  };
}

export function getUndoTimelineState() {
  return state;
}

export function subscribeUndoTimeline(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
